import { useState, useEffect, useCallback } from 'react';
import { EventTimeline } from 'matrix-js-sdk';
import { cloneDeep } from 'lodash';
import { useMatrixClient } from '../context/MatrixContext';
import {
  JOIN_RULES,
  HISTORY_VISIBILITY,
  PERMISSION_EVENTS,
  POWER_LEVELS,
} from '../constants/roomSettings';
import { getRoomDisplayName } from '../utils/gematikRoomUtils';
import logger from '../utils/logger';

/**
 * Deep clone power levels object to prevent state mutations.
 * Preserves all fields including custom notification levels and unknown keys.
 * Uses lodash.cloneDeep for reliable deep cloning across all browsers.
 *
 * @param {Object} powerLevels - The power levels object to clone
 * @returns {Object} - Deep cloned power levels object with all fields preserved
 */
const deepClonePowerLevels = (powerLevels) => {
  if (!powerLevels || typeof powerLevels !== 'object') {
    return {};
  }
  return cloneDeep(powerLevels);
};

/**
 * Validate power levels structure before sending to server.
 * Validates known fields while allowing unknown fields to pass through.
 * This preserves custom notification levels and future Matrix spec additions.
 *
 * @param {Object} powerLevels - Power levels to validate
 * @throws {Error} - If validation fails for known fields
 */
const validatePowerLevels = (powerLevels) => {
  if (!powerLevels || typeof powerLevels !== 'object') {
    throw new Error('Power levels must be an object');
  }

  // Validate known numeric fields (allows unknown fields to pass through)
  const numericFields = [
    'users_default',
    'events_default',
    'state_default',
    'invite',
    'kick',
    'ban',
    'redact',
  ];

  for (const field of numericFields) {
    if (powerLevels[field] !== undefined) {
      const value = powerLevels[field];
      if (typeof value !== 'number' || !Number.isInteger(value)) {
        throw new Error(`Power level field '${field}' must be an integer, got ${typeof value}`);
      }
      if (value < 0) {
        throw new Error(`Power level field '${field}' must be >= 0, got ${value}`);
      }
    }
  }

  // Validate users map if present
  if (powerLevels.users && typeof powerLevels.users === 'object') {
    for (const [userId, level] of Object.entries(powerLevels.users)) {
      if (typeof level !== 'number' || !Number.isInteger(level)) {
        throw new Error(`Power level for user ${userId} must be an integer`);
      }
      if (level < 0) {
        throw new Error(`Power level for user ${userId} must be >= 0`);
      }
    }
  }

  // Validate events map if present
  if (powerLevels.events && typeof powerLevels.events === 'object') {
    for (const [eventType, level] of Object.entries(powerLevels.events)) {
      if (typeof level !== 'number' || !Number.isInteger(level)) {
        throw new Error(`Power level for event ${eventType} must be an integer`);
      }
      if (level < 0) {
        throw new Error(`Power level for event ${eventType} must be >= 0`);
      }
    }
  }
};

/**
 * Hook for managing room settings (security, permissions, notifications)
 * Separate from useRoomEdit which is for participant/avatar management
 *
 * @param {string} roomId - The room ID to manage settings for
 * @returns {Object} Room settings state and actions
 */
export const useRoomSettings = (roomId) => {
  const client = useMatrixClient();
  const [room, setRoom] = useState(null);
  const [roomName, setRoomName] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Security & Privacy settings
  const [joinRule, setJoinRule] = useState(JOIN_RULES.INVITE);
  const [historyVisibility, setHistoryVisibility] = useState(HISTORY_VISIBILITY.JOINED);

  // Permissions settings (power levels)
  const [powerLevels, setPowerLevels] = useState(null);
  const [permissionLevels, setPermissionLevels] = useState({
    [PERMISSION_EVENTS.DEFAULT]: POWER_LEVELS.DEFAULT,
    [PERMISSION_EVENTS.SEND_MESSAGE]: POWER_LEVELS.DEFAULT,
    [PERMISSION_EVENTS.CONFIGURE]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.BAN]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.KICK]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.REDACT]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.INVITE]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.HISTORICAL]: POWER_LEVELS.MODERATOR,
  });

  // Track if user can configure room settings
  const [canConfigure, setCanConfigure] = useState(false);

  // Load room object
  useEffect(() => {
    if (!client || !roomId) {
      setRoom(null);
      setRoomName('');
      setIsLoading(false);
      return;
    }

    const roomObject = client.getRoom(roomId);
    if (roomObject) {
      setRoom(roomObject);
      const displayName = getRoomDisplayName(roomObject);
      setRoomName(displayName);
    }
    setIsLoading(false);
  }, [client, roomId]);

  // Check if current user can configure room settings
  useEffect(() => {
    if (!room || !client) {
      setCanConfigure(false);
      return;
    }

    try {
      const userId = client.getUserId();
      const powerLevelsEvent = room
        .getLiveTimeline()
        .getState(EventTimeline.FORWARDS)
        .getStateEvents('m.room.power_levels', '');
      
      if (!powerLevelsEvent) {
        setCanConfigure(false);
        return;
      }

      const powerLevelsContent = powerLevelsEvent.getContent();
      // The power level required to change the power levels event itself.
      const powerLevelRequired = powerLevelsContent.events?.['m.room.power_levels'] ?? powerLevelsContent.state_default ?? 50;
      const userPowerLevel = powerLevelsContent.users?.[userId] ?? powerLevelsContent.users_default ?? 0;

      // To configure permissions, the user must have the power to send 'm.room.power_levels' state events.
      // This is more specific than just checking against state_default.
      setCanConfigure(userPowerLevel >= powerLevelRequired);
    } catch (error) {
      logger.error('Error checking room configuration permission:', error);
      setCanConfigure(false);
    }
  }, [room, client]);

  // Load current room settings and listen for changes
  useEffect(() => {
    if (!room) return;

    const loadSettings = () => {
      try {
        // Load join rule
        const joinRuleEvent = room.currentState.getStateEvents('m.room.join_rules', '');
        if (joinRuleEvent) {
          const rule = joinRuleEvent.getContent()?.join_rule || JOIN_RULES.INVITE;
          setJoinRule(rule);
        }

        // Load history visibility
        const historyEvent = room.currentState.getStateEvents('m.room.history_visibility', '');
        if (historyEvent) {
          const visibility = historyEvent.getContent()?.history_visibility || HISTORY_VISIBILITY.JOINED;
          setHistoryVisibility(visibility);
        }

        // Load power levels
        const powerLevelsEvent = room.currentState.getStateEvents('m.room.power_levels', '');
        if (powerLevelsEvent) {
          const content = powerLevelsEvent.getContent();
          setPowerLevels(content);

          // Extract permission levels
          const levels = {
            [PERMISSION_EVENTS.DEFAULT]: content.users_default ?? POWER_LEVELS.DEFAULT,
            // Per Matrix spec: m.room.message uses events_default, not events['m.room.message']
            // See: https://spec.matrix.org/v1.11/client-server-api/#mroompower_levels
            [PERMISSION_EVENTS.SEND_MESSAGE]: content.events_default ?? POWER_LEVELS.DEFAULT,
            [PERMISSION_EVENTS.CONFIGURE]: content.state_default ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.BAN]: content.ban ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.KICK]: content.kick ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.REDACT]: content.redact ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.INVITE]: content.invite ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.HISTORICAL]: content.events?.['m.room.history_visibility'] ?? content.state_default ?? POWER_LEVELS.MODERATOR,
          };

          setPermissionLevels(levels);
        }
      } catch (error) {
        logger.error('Error loading room settings:', error);
      }
    };

    // Load settings initially
    loadSettings();

    // Listen for state changes
    const handleStateEvents = (event) => {
      const eventType = event.getType();
      
      if (eventType === 'm.room.join_rules' || 
          eventType === 'm.room.history_visibility' || 
          eventType === 'm.room.power_levels') {
        logger.info('Room settings changed, reloading:', eventType);
        loadSettings();
      }
    };

    room.on('RoomState.events', handleStateEvents);

    return () => {
      room.off('RoomState.events', handleStateEvents);
    };
  }, [room]);

  // Update join rule
  const updateJoinRule = useCallback(async (newRule) => {
    if (!client || !roomId || !canConfigure) {
      throw new Error('Cannot update join rule: insufficient permissions or missing data');
    }

    try {
      await client.sendStateEvent(roomId, 'm.room.join_rules', {
        join_rule: newRule,
      }, '');
      setJoinRule(newRule);
      logger.info('Updated join rule:', newRule);
    } catch (error) {
      logger.error('Error updating join rule:', error);
      throw error;
    }
  }, [client, roomId, canConfigure]);

  // Update history visibility
  const updateHistoryVisibility = useCallback(async (newVisibility) => {
    if (!client || !roomId || !canConfigure) {
      throw new Error('Cannot update history visibility: insufficient permissions or missing data');
    }

    try {
      await client.sendStateEvent(roomId, 'm.room.history_visibility', {
        history_visibility: newVisibility,
      }, '');
      setHistoryVisibility(newVisibility);
      logger.info('Updated history visibility:', newVisibility);
    } catch (error) {
      logger.error('Error updating history visibility:', error);
      throw error;
    }
  }, [client, roomId, canConfigure]);

  // Update permission level for a specific function
  const updatePermissionLevel = useCallback(async (permissionKey, newLevel) => {
    if (!client || !roomId || !canConfigure || !powerLevels) {
      throw new Error('Cannot update permission: insufficient permissions or missing data');
    }

    try {
      // Deep clone current power levels to prevent mutations
      const newPowerLevels = deepClonePowerLevels(powerLevels);

      // Update the specific permission
      switch (permissionKey) {
      case PERMISSION_EVENTS.DEFAULT:
        newPowerLevels.users_default = newLevel;
        break;
      case PERMISSION_EVENTS.SEND_MESSAGE:
        // Per Matrix spec: m.room.message uses events_default, not events['m.room.message']
        // This ensures compatibility with Element and other Matrix clients
        newPowerLevels.events_default = newLevel;
        break;
      case PERMISSION_EVENTS.CONFIGURE:
        newPowerLevels.state_default = newLevel;
        break;
      case PERMISSION_EVENTS.BAN:
        newPowerLevels.ban = newLevel;
        break;
      case PERMISSION_EVENTS.KICK:
        newPowerLevels.kick = newLevel;
        break;
      case PERMISSION_EVENTS.REDACT:
        newPowerLevels.redact = newLevel;
        break;
      case PERMISSION_EVENTS.INVITE:
        newPowerLevels.invite = newLevel;
        break;
      case PERMISSION_EVENTS.HISTORICAL:
        if (!newPowerLevels.events) newPowerLevels.events = {};
        newPowerLevels.events['m.room.history_visibility'] = newLevel;
        break;
      default:
        throw new Error(`Unknown permission key: ${permissionKey}`);
      }

      // Validate power levels structure before sending
      validatePowerLevels(newPowerLevels);

      // Send updated power levels
      await client.sendStateEvent(roomId, 'm.room.power_levels', newPowerLevels, '');
      setPowerLevels(newPowerLevels);
      
      // Update local state
      setPermissionLevels(prev => ({
        ...prev,
        [permissionKey]: newLevel,
      }));

      logger.info('Updated permission level:', { permissionKey, newLevel });
    } catch (error) {
      logger.error('Error updating permission level:', error);
      throw error;
    }
  }, [client, roomId, canConfigure, powerLevels]);

  return {
    // State
    room,
    roomName,
    isLoading,
    canConfigure,
    
    // Security & Privacy
    joinRule,
    historyVisibility,
    
    // Permissions
    permissionLevels,
    powerLevels,
    
    // Actions
    updateJoinRule,
    updateHistoryVisibility,
    updatePermissionLevel,
  };
};

export default useRoomSettings;
